import classNames from 'classnames';
import { useDynamicHeaderMargin } from '~/hooks/useDynamicHeaderMargin';
import { Loading } from '../ui/Loading';
import { NavContainer } from '../ui/NavContainer';
import { Result } from '../ui/Result';
import { AddButton } from '../ui/buttons/AddButton';
import { Outlet } from './Outlet';

export type SplitProps = {
  nav: React.ReactNode;
  filters: React.ReactNode;
  main: React.ReactNode;
  height?: string;

  /** Optionally provide an outlet context */
  context?: unknown;

  /** If true the main section will not have overflow-y-auto */
  disableMainScroll?: boolean;

  /** If true the outlet will be hidden */
  outletHidden?: boolean;
  count?: number;

  /** If true the Split will have no side padding */
  hasBackground?: boolean;

  /** Mobile action button - provide a label string or "none" to hide */
  mobileAction?: string;

  /** Custom outlet content to render instead of the default router Outlet */
  customOutlet?: React.ReactNode;
};

export function Split({
  nav,
  filters,
  main,
  height,
  context,
  disableMainScroll = false,
  outletHidden = false,
  count,
  hasBackground = false,
  mobileAction,
  customOutlet,
}: SplitProps) {
  const fetching = false;
  const { filterRowRef, dynamicMarginTop } = useDynamicHeaderMargin();
  return (
    <>
      {mobileAction && mobileAction !== 'none' && (
        <AddButton label={mobileAction} />
      )}
      <NavContainer nav={nav}>
        <NavContainer.FilterRow ref={filterRowRef}>
          {filters && filters}
        </NavContainer.FilterRow>
      </NavContainer>
      <div
        className={classNames(
          'w-full bg-surface-container-background transition-all duration-200 ease-custom min-h-screen-minus-nav lg:mt-0 lg:flex lg:overflow-y-hidden',
          height ? height : 'lg:h-screen-minus-nav'
        )}
        style={{
          marginTop: dynamicMarginTop,
        }}
      >
        <div className='min-w-0 flex-1'>
          {fetching ? (
            <div className='flex h-48 items-center justify-center'>
              <Loading spinner />
            </div>
          ) : (
            <>
              <div
                data-split-main
                className={classNames(
                  'relative max-w-full overflow-x-hidden lg:mt-0',
                  height ? height : 'lg:h-screen-minus-nav',
                  !disableMainScroll && 'overflow-y-auto',
                  'pb-[25vh] lg:pb-0'
                )}
              >
                <div
                  className={classNames(
                    disableMainScroll ? 'h-full' : 'flex flex-col gap-1 pb-8',
                    hasBackground ? 'h-full !pb-0 lg:px-0' : 'px-3 pt-3 lg:px-4'
                  )}
                >
                  {!disableMainScroll && count !== undefined && (
                    <div className='pb-2'>
                      <Result count={count} />
                    </div>
                  )}
                  {main}
                </div>
              </div>
            </>
          )}
        </div>
        {!outletHidden &&
          (customOutlet ? (
            <div
              data-outlet
              className={classNames([
                'fixed inset-0 z-50 h-screen w-full overflow-hidden bg-white lg:relative lg:bg-transparent',
                'lg:w-rsb xl:w-2rsb',
                height || 'lg:h-screen-minus-nav',
              ])}
            >
              {customOutlet}
            </div>
          ) : (
            <Outlet context={context} height={height} />
          ))}
      </div>
    </>
  );
}
